/**
 * S02 — Thesis: "A THREE-ACT ODYSSEY"
 *
 * Bone background, big serif title sliced by blood-red blade,
 * moodboard collages on left/right margins (desktop only). The
 * three-act preview cards used to live here but were removed —
 * each act now gets its own dedicated section (S03/S04/S05) so
 * repeating them on the thesis page was redundant.
 */

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy } from "@/lib/voice";
import { keyImages, moodboardUrl } from "@/lib/moodboard";

gsap.registerPlugin(ScrollTrigger);

const leftCollage = [
  keyImages.satriales, // 09
  keyImages.sopranosHorse, // 07
  keyImages.churchPew, // 06
];
const rightCollage = [
  keyImages.kitano, // 29
  keyImages.colombianHillside, // 53
  keyImages.truffeNoire, // 57
];

export function S02_Thesis() {
  const copy = sectionCopy.s02;
  const sectionRef = useRef<HTMLElement | null>(null);
  const bladeRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      // Blade slice animation tied to scroll into hero
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 70%",
          end: "top top",
          scrub: 1,
        },
      });
      tl.to(bladeRef.current, {
        width: "108%",
        duration: 1,
        ease: "expo.inOut",
      }).to(
        bottomRef.current,
        { y: 8, x: 3, duration: 0.5, ease: "power2.out" },
        "-=0.2",
      );
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="s02"
      className="section"
      style={{
        backgroundColor: "var(--bone)",
        color: "var(--ink)",
        flexDirection: "column",
        padding: "14vh var(--space-lg) 12vh",
        minHeight: "100vh",
        gap: "var(--space-xl)",
      }}
    >
      <MonumentType word={copy.monument} theme="bone" />

      {/* Left collage column */}
      <div
        className="s02-collage-left"
        style={{
          position: "absolute",
          top: "14vh",
          left: "var(--space-lg)",
          bottom: "auto",
          width: "12vw",
          height: "64vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: "var(--space-md)",
          zIndex: 1,
        }}
      >
        {leftCollage.map((idx, i) => (
          <div
            key={idx}
            style={{
              flex: 1,
              backgroundImage: `url(${moodboardUrl(idx)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "grayscale(100%) contrast(1.15) brightness(0.85)",
              transform: `rotate(${i % 2 === 0 ? -1.5 : 1.5}deg)`,
              boxShadow: "0 20px 50px rgba(5,5,5,0.2)",
            }}
          />
        ))}
      </div>

      {/* Right collage column */}
      <div
        className="s02-collage-right"
        style={{
          position: "absolute",
          top: "14vh",
          right: "var(--space-lg)",
          bottom: "auto",
          width: "12vw",
          height: "64vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: "var(--space-md)",
          zIndex: 1,
        }}
      >
        {rightCollage.map((idx, i) => (
          <div
            key={idx}
            style={{
              flex: 1,
              backgroundImage: `url(${moodboardUrl(idx)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "grayscale(100%) contrast(1.15) brightness(0.85)",
              transform: `rotate(${i % 2 === 0 ? 1.5 : -1.5}deg)`,
              boxShadow: "0 20px 50px rgba(5,5,5,0.2)",
            }}
          />
        ))}
      </div>

      {/* Meta label */}
      <div
        className="type-meta-sm"
        style={{
          position: "relative",
          color: "var(--fog)",
          letterSpacing: "var(--ls-meta-wide)",
          zIndex: 3,
          textAlign: "center",
        }}
      >
        {copy.meta}
      </div>

      {/* Thesis title — sliced */}
      <div
        style={{
          position: "relative",
          textAlign: "center",
          zIndex: 2,
          maxWidth: "72vw",
          margin: "0 auto",
        }}
      >
        <h2
          className="type-hero-lg"
          style={{
            color: "var(--ink)",
            margin: 0,
            clipPath: "polygon(0 0, 100% 0, 100% 50%, 0 50%)",
          }}
        >
          {copy.hero}
        </h2>
        <div
          ref={bottomRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
          }}
        >
          <h2
            className="type-hero-lg"
            style={{
              color: "var(--ink)",
              margin: 0,
              clipPath: "polygon(0 50%, 100% 50%, 100% 100%, 0 100%)",
            }}
          >
            {copy.hero}
          </h2>
        </div>
        <div
          ref={bladeRef}
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "50%",
            left: "-4%",
            width: 0,
            height: "3px",
            background: "var(--blood)",
            boxShadow: "0 0 24px var(--blood)",
            transform: "translateY(-50%)",
            zIndex: 10,
          }}
        />
      </div>

      {/* Subtitle */}
      {copy.subhero && (
        <div
          className="type-voice"
          style={{
            color: "var(--fog)",
            fontSize: "clamp(1rem, 1.4vw, 1.5rem)",
            zIndex: 3,
            textAlign: "center",
            maxWidth: "60ch",
            margin: "0 auto",
            marginTop: "var(--space-sm)",
            lineHeight: 1.55,
            fontStyle: "italic",
          }}
        >
          {copy.subhero}
        </div>
      )}
    </section>
  );
}
